// The one place the client talks to the server.
//
// Every response shape is typed against the server's own domain types, imported
// directly — the client cannot drift from the API because there is only one
// definition of an Experiment in the repository.

import type {
  Allergy, AiMode, Clinician, Confounder, DailyCheckIn, Experiment, HealthEvent, HealthNote,
  Intervention, IsoDate, MedicalCondition, Medication, MetricDefinition, OccurrenceStatus,
  OverallStatus, Prescription, Reminder, ReminderOccurrence, Schedule, Settings,
} from '@shared';

export type {
  Allergy, AiMode, Clinician, Confounder, DailyCheckIn, Experiment, HealthEvent, HealthNote,
  Intervention, IsoDate, MedicalCondition, Medication, MetricDefinition, OccurrenceStatus,
  OverallStatus, Prescription, Reminder, ReminderOccurrence, Schedule, Settings,
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : {},
    ...init,
  });

  if (!response.ok) {
    // Prefer the server's own message; fall back to the status when the body is
    // not JSON (a proxy error page, say).
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* keep the fallback */ }
    throw new ApiError(response.status, message);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

const get = <T>(path: string): Promise<T> => request<T>(path);
const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

// --- Shapes the server returns ---------------------------------------------

export interface AuthState {
  needs_setup: boolean;
  signed_in: boolean;
  user: { name: string; email: string } | null;
}

export interface TodayAction {
  occurrence_id: string;
  reminder_id: string;
  experiment_id: string | null;
  experiment_name: string | null;
  intervention_id: string | null;
  kind: string;
  title: string;
  instructions: string | null;
  dose: string | null;
  due_at: string;
  due_time: string;
  status: OccurrenceStatus;
  snoozed_until: string | null;
  overdue: boolean;
}

export interface TodayExperiment {
  id: string;
  name: string;
  status: string;
  phase: string;
  day_number: number | null;
  total_days: number | null;
  progress: number | null;
  adherence_percent: number | null;
  checkin_done: boolean;
  primary_metric: {
    name: string;
    unit: string | null;
    change_percent: number | null;
    direction: string;
    headline: string;
  } | null;
  pending_question: { date: IsoDate; question: string } | null;
}

export interface RedFlag {
  id: string;
  level: 'discuss' | 'urgent';
  message: string;
  rule_id: string;
  local_date: IsoDate;
}

export interface TodaySummary {
  date: IsoDate;
  local_time: string;
  greeting: string;
  actions: TodayAction[];
  experiments: TodayExperiment[];
  red_flags: RedFlag[];
  upcoming: Array<{ type: string; title: string; date: IsoDate; detail: string | null }>;
  recent_events: HealthEvent[];
}

export interface Comparison {
  baseline_average: number | null;
  current_average: number | null;
  baseline_count: number;
  current_count: number;
  absolute_change: number | null;
  percentage_change: number | null;
  direction: 'improved' | 'worse' | 'unchanged' | 'unknown';
  has_enough_data: boolean;
}

export interface Adherence {
  expected: number;
  completed: number;
  skipped: number;
  missed: number;
  upcoming: number;
  percent: number | null;
}

export interface MetricSeriesPoint {
  local_date: IsoDate;
  value: number | null;
  rolling: number | null;
  phase: string;
}

export interface MetricAnalysis {
  metric: MetricDefinition;
  baseline: { count: number; mean: number | null; median: number | null; min: number | null; max: number | null };
  current: { count: number; mean: number | null; median: number | null; min: number | null; max: number | null };
  comparison: Comparison;
  trend_per_day: number | null;
  description: string;
  series: MetricSeriesPoint[];
}

export interface ExperimentDetail {
  experiment: Experiment;
  today: IsoDate;
  progress: {
    phase: string;
    day_in_phase: number | null;
    phase_length: number | null;
    day_number: number | null;
    total_days: number | null;
    progress: number | null;
  };
  metrics: MetricAnalysis[];
  primary: MetricAnalysis | null;
  adherence: Adherence;
  adherence_band: 'good' | 'fair' | 'poor' | 'unknown';
  per_intervention: Array<{ intervention_id: string; name: string; adherence: Adherence; miss_streak: number }>;
  confounders: Confounder[];
  side_effects: Array<{ id: string; local_date: IsoDate; name: string }>;
  missing_days: IsoDate[];
  interventions: Intervention[];
  reminders: Reminder[];
  checkins: DailyCheckIn[];
  latest_complete_week: number | null;
}

export interface CheckInForm {
  experiment_id: string;
  local_date: IsoDate;
  metrics: MetricDefinition[];
  existing: Record<string, number | string | null>;
  overall_status: OverallStatus | null;
  notes: string | null;
  confounders: string[];
  side_effects: string[];
}

export interface CheckInResult {
  local_date: IsoDate;
  recorded: number;
  level: 'none' | 'discuss' | 'urgent';
  flags: Array<{ rule_id: string; level: 'discuss' | 'urgent'; message: string }>;
  escalate: boolean;
}

export interface TemplateSummary {
  id: string;
  name: string;
  category: string;
  description: string;
  suggested_baseline_days: number;
  suggested_duration_days: number;
  goal_examples: string[];
}

export interface TemplateDetail extends TemplateSummary {
  metrics: Array<{
    name: string; prompt: string; type: MetricDefinition['type']; unit?: string;
    min_value?: number; max_value?: number; higher_is_better: boolean;
    required: boolean; is_primary?: boolean; choices?: string[];
  }>;
  interventions: Array<{
    name: string; type: Intervention['type']; example?: string; instructions?: string;
    technique?: string[]; schedule: Schedule; food_timing?: string; side_effects?: string[];
  }>;
  confounders: string[];
}

export interface WeeklyReviewResult {
  week: number;
  start: IsoDate;
  end: IsoDate;
  data: {
    week: number;
    metrics: Array<{
      metric_id: string; name: string; unit: string | null;
      week_average: number | null; baseline_average: number | null;
      change_percent: number | null; direction: string;
    }>;
    adherence: { completed: number; expected: number; percent: number | null; band: string; missed: number; skipped: number };
    confounders: string[];
    side_effects: string[];
    logged_days: number;
    expected_days: number;
  };
  review: {
    summary: string;
    adherence_comment: string | null;
    confounders: string[];
    side_effects: string[];
    data_quality_notes: string[];
    confidence: string;
    recommended_question: string | null;
  } | null;
}

export interface FinalReportResult {
  analysis: ExperimentDetail;
  report: {
    summary: string;
    what_changed: string[];
    adherence_comment: string | null;
    side_effects_comment: string | null;
    confounders_comment: string | null;
    data_quality_notes: string[];
    confidence: string;
    suggested_next_step: string;
    next_step_reason: string;
    questions_for_clinician: string[];
  } | null;
}

export interface DoctorSummary {
  generated_at: string;
  problem: string;
  experiment_name: string;
  dates: { baseline: string | null; intervention: string | null };
  interventions: Array<{ name: string; dose: string | null; schedule: string; instructions: string | null }>;
  adherence: { completed: number; expected: number; percent: number | null };
  metrics: Array<{
    name: string; unit: string | null; baseline_average: number | null;
    intervention_average: number | null; change_percent: number | null;
    direction: string; readings: number;
  }>;
  side_effects: string[];
  confounders: string[];
  observations: string[];
  current_medications: Array<{ name: string; strength: string | null; dose: string | null; frequency: string | null; purpose: string | null }>;
  allergies: Array<{ substance: string; reaction: string | null }>;
  conditions: Array<{ name: string; status: string | null }>;
  questions_for_clinician: string[];
  disclaimer: string;
}

// --- The API ---------------------------------------------------------------

export const api = {
  auth: {
    state: () => get<AuthState>('/auth/state'),
    setup: (body: { name: string; email: string; password: string }) => post<{ user: AuthState['user'] }>('/auth/setup', body),
    login: (body: { email: string; password: string }) => post<{ user: AuthState['user'] }>('/auth/login', body),
    logout: () => post<{ ok: boolean }>('/auth/logout'),
    changePassword: (body: { current_password: string; new_password: string }) =>
      post<{ ok: boolean }>('/auth/password', body),
  },

  today: () => get<TodaySummary>('/api/today'),
  tick: () => post<Record<string, number>>('/api/scheduler/tick'),

  experiments: {
    list: (status?: string) => get<{ experiments: Experiment[] }>(`/api/experiments${status ? `?status=${status}` : ''}`),
    detail: (id: string) => get<ExperimentDetail>(`/api/experiments/${id}`),
    create: (body: unknown) => post<{ experiment: Experiment }>('/api/experiments', body),
    update: (id: string, body: unknown) => patch<{ experiment: Experiment }>(`/api/experiments/${id}`, body),
    setStatus: (id: string, status: string) => post<{ experiment: Experiment }>(`/api/experiments/${id}/status`, { status }),
    remove: (id: string) => del<{ ok: boolean }>(`/api/experiments/${id}`),
    templates: () => get<{ templates: TemplateSummary[] }>('/api/experiments/templates'),
    template: (id: string) => get<{ template: TemplateDetail }>(`/api/experiments/templates/${id}`),
    checkInForm: (id: string, date?: string) =>
      get<CheckInForm>(`/api/experiments/${id}/checkin${date ? `?date=${date}` : ''}`),
    submitCheckIn: (id: string, body: unknown) => post<CheckInResult>(`/api/experiments/${id}/checkin`, body),
    addIntervention: (id: string, body: unknown) => post<{ intervention: Intervention }>(`/api/experiments/${id}/interventions`, body),
    updateIntervention: (interventionId: string, body: unknown) =>
      patch<{ intervention: Intervention }>(`/api/experiments/interventions/${interventionId}`, body),
  },

  reminders: {
    act: (occurrenceId: string, body: { action: 'done' | 'snooze' | 'skip' | 'note'; note?: string | null; reason?: string | null; snooze_minutes?: number }) =>
      post<{ occurrence: ReminderOccurrence }>(`/api/reminders/occurrences/${occurrenceId}/act`, body),
    setEnabled: (id: string, enabled: boolean) => post<{ reminder: Reminder }>(`/api/reminders/${id}/enabled`, { enabled }),
    list: (experimentId?: string) =>
      get<{ reminders: Array<Reminder & { schedule_text: string }> }>(`/api/reminders${experimentId ? `?experiment_id=${experimentId}` : ''}`),
  },

  ai: {
    status: () => get<{ enabled: boolean; mode: AiMode; configured: boolean }>('/api/ai/status'),
    question: (experimentId: string, force = false) =>
      get<{ question: { question: string; reason: string; topic: string } | null }>(
        `/api/ai/experiments/${experimentId}/question${force ? '?force=true' : ''}`),
    answerQuestion: (experimentId: string, date: string, answer: string) =>
      post<{ ok: boolean }>(`/api/ai/experiments/${experimentId}/question/answer`, { date, answer }),
    weekly: (experimentId: string, week: number, force = false) =>
      get<WeeklyReviewResult>(`/api/ai/experiments/${experimentId}/weekly/${week}${force ? '?force=true' : ''}`),
    report: (experimentId: string, force = false) =>
      get<FinalReportResult>(`/api/ai/experiments/${experimentId}/report${force ? '?force=true' : ''}`),
    hypothesis: (body: { description: string; goal?: string; interventions?: string[] }) =>
      post<{ hypothesis: { hypothesis: string; suggested_primary_metric: string; suggested_baseline_days: number; suggested_duration_days: number; design_warnings: string[] } }>(
        '/api/ai/hypothesis', body),
  },

  medical: {
    medications: (activeOnly = false) => get<{ medications: Medication[] }>(`/api/medical/medications${activeOnly ? '?active=true' : ''}`),
    createMedication: (body: unknown) => post<{ medication: Medication }>('/api/medical/medications', body),
    updateMedication: (id: string, body: unknown) => patch<{ medication: Medication }>(`/api/medical/medications/${id}`, body),
    removeMedication: (id: string) => del<{ ok: boolean }>(`/api/medical/medications/${id}`),
    medicationHistory: (id: string) => get<{ changes: Array<{ field: string; previous_value: string | null; new_value: string | null; changed_at: string }> }>(`/api/medical/medications/${id}/history`),

    prescriptions: () => get<{ prescriptions: Prescription[] }>('/api/medical/prescriptions'),
    createPrescription: (body: unknown) => post<{ prescription: Prescription }>('/api/medical/prescriptions', body),
    removePrescription: (id: string) => del<{ ok: boolean }>(`/api/medical/prescriptions/${id}`),

    profile: () => get<{ conditions: MedicalCondition[]; allergies: Allergy[]; clinicians: Clinician[] }>('/api/medical/profile'),
    createCondition: (body: unknown) => post<{ condition: MedicalCondition }>('/api/medical/conditions', body),
    removeCondition: (id: string) => del<{ ok: boolean }>(`/api/medical/conditions/${id}`),
    createAllergy: (body: unknown) => post<{ allergy: Allergy }>('/api/medical/allergies', body),
    removeAllergy: (id: string) => del<{ ok: boolean }>(`/api/medical/allergies/${id}`),
    createClinician: (body: unknown) => post<{ clinician: Clinician }>('/api/medical/clinicians', body),
    removeClinician: (id: string) => del<{ ok: boolean }>(`/api/medical/clinicians/${id}`),

    notes: (experimentId?: string) => get<{ notes: HealthNote[] }>(`/api/medical/notes${experimentId ? `?experiment_id=${experimentId}` : ''}`),
    createNote: (body: unknown) => post<{ note: HealthNote }>('/api/medical/notes', body),
    removeNote: (id: string) => del<{ ok: boolean }>(`/api/medical/notes/${id}`),

    timeline: (types?: string) => get<{ events: HealthEvent[] }>(`/api/medical/timeline${types ? `?types=${types}` : ''}`),
    redFlags: () => get<{ flags: RedFlag[] }>('/api/medical/red-flags'),
    acknowledgeRedFlag: (id: string) => post<{ ok: boolean }>(`/api/medical/red-flags/${id}/acknowledge`),
    search: (q: string) => get<{ results: Array<{ type: string; id: string; title: string; detail: string | null }> }>(`/api/medical/search?q=${encodeURIComponent(q)}`),
  },

  push: {
    key: () => get<{ key: string | null; configured: boolean }>('/api/push/key'),
    status: () => get<{ configured: boolean; devices: number }>('/api/push/status'),
    subscribe: (sub: unknown) => post<{ ok: boolean; devices: number }>('/api/push/subscribe', sub),
    unsubscribe: (endpoint: string) => post<{ ok: boolean; devices: number }>('/api/push/unsubscribe', { endpoint }),
    test: () => post<{ sent: number; failed: number; devices: number }>('/api/push/test'),
  },

  settings: {
    get: () => get<{ settings: Settings; user: { name: string; email: string }; server: { push_configured: boolean; ai_configured: boolean } }>('/api/settings'),
    update: (body: Partial<Settings>) => patch<{ settings: Settings }>('/api/settings', body),
    doctorSummary: (experimentId: string, questions: string[]) =>
      get<{ summary: DoctorSummary }>(`/api/settings/export/experiment/${experimentId}/doctor?questions=${encodeURIComponent(questions.join('\n'))}`),
    backupUrl: '/api/settings/export/backup.json',
    csvUrl: (experimentId: string) => `/api/settings/export/experiment/${experimentId}.csv`,
  },
};
